import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import api from '../api';

const ROLES = ['worker', 'manager', 'admin'];
const FACILITIES = ['M4-LB', 'M4-PTW'];

const selectStyle = {
  width:'100%', padding:'8px 10px', border:'1.5px solid var(--gray5)',
  borderRadius:8, fontSize:14, background:'#fff',
};

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'worker', facility: '' });

  // Edit state
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/users');
      setUsers(data);
    } catch { setError('Failed to load users'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addUser = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      await api.post('/users', form);
      setForm({ name: '', email: '', password: '', role: 'worker', facility: '' });
      setShowAdd(false); load();
    } catch (err) { setError(err.response?.data?.error || 'Failed to create user'); }
    finally { setSaving(false); }
  };

  const saveUser = async (e, userId) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const payload = { name: editForm.name, email: editForm.email, role: editForm.role, facility: editForm.facility || null, badge_id: editForm.badge_id || null, badge_id_2: editForm.badge_id_2 || null };
      if (editForm.password) payload.password = editForm.password;
      await api.patch(`/users/${userId}`, payload);
      setEditId(null); load();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to save';
      setError(msg);
      if (msg.toLowerCase().includes('badge')) {
        setEditForm(f => ({ ...f, badge_id: '', badge_id_2: '' }));
      }
    }
    finally { setSaving(false); }
  };

  const deactivate = async (u) => {
    if (!window.confirm(`Remove ${u.name}? They will no longer be able to log in.`)) return;
    try { await api.patch(`/users/${u.id}/deactivate`); load(); }
    catch (err) { setError(err.response?.data?.error || 'Failed'); }
  };

  const reactivate = async (u) => {
    try { await api.patch(`/users/${u.id}/reactivate`); load(); }
    catch { setError('Failed to reactivate'); }
  };

  const startEdit = (u) => {
    setEditId(u.id);
    setEditForm({ name: u.name, email: u.email, role: u.role, facility: u.facility || '', password: '', badge_id: u.badge_id || '', badge_id_2: u.badge_id_2 || '' });
    setShowAdd(false);
  };

  const active = users.filter(u => u.is_active);
  const inactive = users.filter(u => !u.is_active);

  if (loading) return <div className="loading-screen">Loading…</div>;

  return (
    <div className="page">
      <div className="topbar">
        <button className="btn btn-ghost" style={{ padding:'6px 12px', fontSize:13 }} onClick={() => navigate('/dashboard')}>
          ← Dashboard
        </button>
        <h1>Users</h1>
        <div />
      </div>

      <div className="content" style={{ maxWidth:680 }}>
        {error && <div className="error-msg" style={{ marginBottom:12 }}>{error}</div>}

        <div className="card" style={{ marginBottom:16 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: showAdd ? 14 : 0 }}>
            <div className="section-title" style={{ marginBottom:0 }}>Active users ({active.length})</div>
            <button className="btn btn-primary" style={{ padding:'6px 14px', fontSize:13 }}
              onClick={() => { setShowAdd(s => !s); setEditId(null); }}>
              {showAdd ? 'Cancel' : '+ Add user'}
            </button>
          </div>

          {/* Add user form */}
          {showAdd && (
            <form onSubmit={addUser} style={{ borderTop:'1px solid var(--gray5)', paddingTop:14 }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <div className="field"><label>Name</label>
                  <input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="Full name" required />
                </div>
                <div className="field"><label>Email</label>
                  <input type="email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} placeholder="user@company.com" required />
                </div>
                <div className="field"><label>Password</label>
                  <input type="password" value={form.password} onChange={e=>setForm(f=>({...f,password:e.target.value}))} placeholder="Temporary password" required />
                </div>
                <div className="field"><label>Role</label>
                  <select value={form.role} onChange={e=>setForm(f=>({...f,role:e.target.value}))} style={selectStyle}>
                    {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div className="field"><label>Facility</label>
                  <select value={form.facility} onChange={e=>setForm(f=>({...f,facility:e.target.value}))} style={selectStyle}>
                    <option value="">— Unassigned —</option>
                    {FACILITIES.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
              </div>
              <button className="btn btn-success" type="submit" disabled={saving} style={{ marginTop:4 }}>
                {saving ? 'Creating…' : 'Create user'}
              </button>
            </form>
          )}

          {/* Active users list */}
          <div style={{ marginTop: showAdd ? 14 : 10 }}>
            {active.length === 0 && (
              <div style={{ color:'var(--gray3)', fontSize:14, padding:'8px 0' }}>No active users.</div>
            )}
            {active.map(u => (
              <div key={u.id} style={{ borderBottom:'1px solid var(--gray5)' }}>
                {editId === u.id ? (
                  /* Edit form */
                  <form onSubmit={e => saveUser(e, u.id)} style={{ padding:'12px 0' }}>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
                      <div className="field" style={{ marginBottom:0 }}><label>Name</label>
                        <input value={editForm.name} onChange={e=>setEditForm(f=>({...f,name:e.target.value}))} required />
                      </div>
                      <div className="field" style={{ marginBottom:0 }}><label>Email</label>
                        <input type="email" value={editForm.email} onChange={e=>setEditForm(f=>({...f,email:e.target.value}))} required />
                      </div>
                      <div className="field" style={{ marginBottom:0 }}>
                        <label>New password <span style={{ fontWeight:400, color:'var(--gray3)' }}>(leave blank to keep)</span></label>
                        <input type="password" value={editForm.password} onChange={e=>setEditForm(f=>({...f,password:e.target.value}))} placeholder="Enter new password" />
                      </div>
                      <div className="field" style={{ marginBottom:0 }}><label>Role</label>
                        <select value={editForm.role} onChange={e=>setEditForm(f=>({...f,role:e.target.value}))} style={selectStyle}>
                          {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </div>
                      <div className="field" style={{ marginBottom:0 }}><label>Facility</label>
                        <select value={editForm.facility} onChange={e=>setEditForm(f=>({...f,facility:e.target.value}))} style={selectStyle}>
                          <option value="">— Unassigned —</option>
                          {FACILITIES.map(f => <option key={f} value={f}>{f}</option>)}
                        </select>
                      </div>
                      <div className="field" style={{ marginBottom:0 }}>
                        <label>Badge ID <span style={{ fontWeight:400, color:'var(--gray3)' }}>(scan or type)</span></label>
                        <input value={editForm.badge_id} onChange={e=>setEditForm(f=>({...f,badge_id:e.target.value}))} placeholder="Scan RFID badge or leave blank" />
                      </div>
                      <div className="field" style={{ marginBottom:0 }}>
                        <label>Badge ID 2 <span style={{ fontWeight:400, color:'var(--gray3)' }}>(optional second badge)</span></label>
                        <input value={editForm.badge_id_2} onChange={e=>setEditForm(f=>({...f,badge_id_2:e.target.value}))} placeholder="Scan second RFID badge or leave blank" />
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:8 }}>
                      <button className="btn btn-success" type="submit" style={{ padding:'5px 14px', fontSize:13 }} disabled={saving}>
                        {saving ? 'Saving…' : 'Save'}
                      </button>
                      <button className="btn btn-ghost" type="button" style={{ padding:'5px 14px', fontSize:13 }} onClick={() => setEditId(null)}>
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  /* View row */
                  <div style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 0' }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:600, fontSize:14 }}>
                        {u.name}
                        {u.id === currentUser.id && <span style={{ fontSize:12, color:'var(--gray3)', marginLeft:6 }}>(you)</span>}
                      </div>
                      <div style={{ fontSize:13, color:'var(--gray3)' }}>{u.email}</div>
                      <div style={{ fontSize:12, color:'var(--gray3)', display:'flex', gap:10 }}>
                        <span style={{ color: u.facility ? 'var(--blue)' : 'var(--gray4)' }}>
                          {u.facility || 'No facility'}
                        </span>
                        <span style={{ color: u.badge_id ? 'var(--green)' : 'var(--gray4)' }}>
                          {u.badge_id ? (u.badge_id_2 ? '2 badges assigned' : '1 badge assigned') : 'No badge'}
                        </span>
                      </div>
                    </div>
                    <span className={`badge badge-${u.role === 'admin' ? 'in_progress' : u.role === 'manager' ? 'on_hold' : 'pending'}`}>
                      {u.role}
                    </span>
                    <button className="btn btn-ghost" style={{ padding:'4px 12px', fontSize:13 }} onClick={() => startEdit(u)}>
                      Edit
                    </button>
                    {u.id !== currentUser.id && (
                      <button className="btn btn-danger" style={{ padding:'4px 12px', fontSize:13 }} onClick={() => deactivate(u)}>
                        Remove
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Inactive users */}
        {inactive.length > 0 && (
          <div className="card">
            <div className="section-title">Deactivated users ({inactive.length})</div>
            {inactive.map(u => (
              <div key={u.id} style={{
                display:'flex', alignItems:'center', gap:12,
                padding:'10px 0', borderBottom:'1px solid var(--gray5)', opacity:0.6,
              }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:600, fontSize:14 }}>{u.name}</div>
                  <div style={{ fontSize:13, color:'var(--gray3)' }}>{u.email}</div>
                </div>
                <span className="badge badge-pending">{u.role}</span>
                <button className="btn btn-ghost" style={{ padding:'4px 12px', fontSize:13 }} onClick={() => reactivate(u)}>
                  Restore
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
